import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
	RequestWorker,
	RequestBacklog,
	RequestFilters,
	RequestWorkItem,
} from '../../types/Work/Request'
import { getAssigneesRequesters, getFilteredRequests } from '../../services/inspectionsService'

interface InspectionsLogContextType {
	totalRequestCount: number
	requests?: RequestWorkItem[]
	filters: RequestFilters
	setFilters: React.Dispatch<React.SetStateAction<RequestFilters>>
	requestsLoading: boolean
	workersLoading: boolean
	assignees: RequestWorker[]
	requesters: RequestWorker[]
	loadRequests: () => Promise<void>
}

const InspectionsLogContext = createContext<InspectionsLogContextType | null>(null)

const useInspectionsLogContext = (): InspectionsLogContextType => {
	const InspectionsLogState = useContext(InspectionsLogContext)
	if (InspectionsLogState === null) {
		throw new Error(
			'InspectionsLogContext not found. Try wrapping a parent component with <InspectionsLogContext.Provider>.',
		)
	}
	return InspectionsLogState
}

type InspectionsLogProviderProps = {
	children: ReactNode
}

const InspectionsLogProvider = ({ children }: InspectionsLogProviderProps) => {
	const [totalRequestCount, setTotalRequestCount] = useState(0)
	const [requests, setRequests] = useState<RequestWorkItem[]>()
	const [filters, setFilters] = useState<RequestFilters>({
		requestId: null,
		page: 1,
		searchWorkItem: '',
		assignedToId: null,
		requestedById: null,
		inspectedOn: null,
		requestedOn: null,
		status: '',
	})
	const [requestsLoading, setRequestsLoading] = useState(false)
	const [workersLoading, setWorkersLoading] = useState(false)
	const [assignees, setAssignees] = useState<RequestWorker[]>([])
	const [requesters, setRequesters] = useState<RequestWorker[]>([])

	const loadRequests = useCallback(async () => {
		setRequestsLoading(true)
		const result = await getFilteredRequests(filters)
		setTotalRequestCount(result.totalRequestsCount)
		setRequests(result.inspectionRequests)
		setRequestsLoading(false)
	}, [filters])

	useEffect(() => {
		const handler = setTimeout(() => {
			loadRequests()
		}, 500)

		return () => clearTimeout(handler)
	}, [loadRequests])

	useEffect(() => {
		;(async () => {
			setWorkersLoading(true)
			const result = await getAssigneesRequesters()
			setAssignees(result.assignees)
			setRequesters(result.requesters)
			setWorkersLoading(false)
		})()
	}, [])

	const value = useMemo(
		() => ({
			totalRequestCount,
			requests,
			filters,
			setFilters,
			requestsLoading,
			workersLoading,
			assignees,
			requesters,
			loadRequests,
		}),
		[
			totalRequestCount,
			requests,
			filters,
			requestsLoading,
			workersLoading,
			assignees,
			requesters,
			loadRequests,
		],
	)

	return (
		<InspectionsLogContext.Provider value={value}>
			{children}
		</InspectionsLogContext.Provider>
	)
}
export { useInspectionsLogContext, InspectionsLogProvider }
